/* eslint-disable max-lines */
import * as Yup from "yup";

import { format } from "@gamedev-web/gamedev-sdk-core";
import { PricingOptions, SensitiveContentStatic, YesNoChoice } from "@gamedev-web/one-gaming-crm";

import { AaaShippingType } from "../../models/aaaGameConceptModels";
import { LocalPlayerModes, OnlinePlayerModes } from "../../models/playerModes";
import { TargetPlatforms } from "../../models/targetPlatforms";
import { countryCrmCodeToIso } from "../../server/crm/api/utils/countryCrmCodeToIso";
import { countryValidations } from "../../server/partnerCenter/data/validations";
import { hasHandheldOptimizationRequiredPlatform } from "../handheldOptimizationHelpers";
import { hasXboxPlayAnywhereRequiredPlatforms } from "../xboxPlayAnywhereHelpers";

const validEmailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const validPhoneNumberRegex = /^[+]{0,1}([.\-()\s]*[0-9]{0,1}[)]*){0,13}$/;
const validUrlRegex =
  /^(http(s){0,1}:\/\/)?([a-zA-Z0-9-_]{0,256}.)?[a-zA-Z0-9-_]{1,256}\.[a-zA-Z0-9.]{1,10}(\/[a-zA-Z0-9./\\@#%?$&!,-_~]{0,1024}){0,1}$/;

// ProfileVet blocked characters: < > % $ [ ] { } ?
// eslint-disable-next-line no-useless-escape
const blockedCharsRegex = /[<>%$\[\]{}?]/;
// State/Province: exactly 2 letters (US/CA)
const stateProvinceRegex = /^[A-Za-z]{2}$/;
const placeholderGameTitles = new Set(["tba", "tbd", "to be determined", "working title"]);

const isSpecificGameTitle = (value: string | undefined): boolean => {
  if (!value) {
    return true;
  }

  const normalizedTitle = value
    .trim()
    .replace(/^\((.*)\)$/, "$1")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
  return !placeholderGameTitles.has(normalizedTitle);
};

export const pitchInputsMaxLength = {
  nameCompanyOrganization: 1500,
  emailAddress: 100,
  xboxContact: 1000,
  programConsiderationTypeOther: 500,
  reasonOfGoodFit: 5000,
  specificAspectToPrototype: 3000,
  studioPointOfContact: 200,
  countryLocated: 1000,
  publisherName: 2000,
  studioEstablished: 500,
  introductionDeckStudio: 500,
  companyWebsite: 1500,
  listGameLaunchedOnConsole: 1000,
  gameTitle: 300,
  releasedPlatformsOther: 300,
  steamReleaseWindow: 300,
  xboxReleaseWindow: 300,
  genreOther: 500,
  engineOther: 150,
  gameplayDuration: 500,
  gameIntroDeckLink: 500,
  otherInfoLinks: 1000,
  buildLinks: 1745,
  videoLinks: 1745,
  gameDescription: 5000,
  targetAudience: 3000,
  competitorTitles: 2000,
  uniqueFactor: 3500,
  feelingsEvoked: 3500,
  pivotalMoment: 3500,
  postLaunchSupportPlans: 3500,
  sensitiveContentDetails: 3500,
  fundingSoughtDetails: 3500,
  otherSupportNeeded: 3500,
  studioBackground: 5000,
  activeInLocalGameDev: 3500,
};

export const gameConceptInputsMaxLength = {
  gameTitle: 300,
  gameDevelopers: 32768,
  hook: 32768,
  gameplayVideosLinks: 32768,
  gameplayDetails: 32768,
  gameBuildLinks: 2000,
  howItWillGenerateMoney: 4000,
  retentionMechanics: 4000,
  inGamePricePoints: 4000,
  whatPlayersCanSpendOn: 4000,
  timeUntilAPlayerIsPromptedToPay: 4000,
  subscriptionModel: 500,
  howItemsCanBeGifted: 4000,
  prevReleases: 500,
  primaryPublisherName: 100,
  certResponsiblePartner: 100,
  otherLanguages: 1000,
  xboxSeriesXSAdvantage: 32768,
  xboxSeriesXPerfTargets: 256,
  xboxSeriesSPerfTargets: 256,
  internetConnectionDetails: 32768,
  ugcDetails: 4000,
  releaseNotes: 100,
  gameplayDuration: 500,
  levelsDetails: 4000,
  sensitiveContentDetails: 3500,
};

const contactInputsMaxLength = {
  firstName: 50,
  lastName: 50,
  addressLine1: 50,
  addressLine2: 50,
  city: 64,
  postalCode: 15,
  email: 254,
  phone: 20,
};

const targetPlatformsValidationSchema = (localizedStrings: { [key: string]: string }) =>
  Yup.array()
    .required()
    .of(
      Yup.object().shape({
        platform: Yup.string().required(),
        selected: Yup.boolean().required(),
        releaseDate: Yup.string().when("selected", {
          is: true,
          then: Yup.string().required(localizedStrings.required),
        }),
      })
    )
    .test(
      localizedStrings.targetSystemsAndPlatforms,
      localizedStrings.targetSystemsAndPlatformsRequiredFieldsSubtitle,
      platformValues => {
        if (!platformValues) {
          return false;
        }
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        const platforms = [
          TargetPlatforms.xboxSeriesXS,
          TargetPlatforms.xboxOne,
          TargetPlatforms.windowsStore,
        ];
        for (const { platform, selected, releaseDate } of platformValues) {
          if (releaseDate && selected && platform) {
            if (platforms.includes(TargetPlatforms[platform]) && releaseDate.match(regex)) {
              return true;
            }
          }
        }
        return false;
      }
    )
    .test(
      "xbox-play-anywhere-required-platforms",
      localizedStrings.xboxPlayAnywherePlatformsRequired,
      function (platformValues) {
        return hasXboxPlayAnywhereRequiredPlatforms(this.parent?.xboxPlayAnywhere, platformValues);
      }
    )
    .test(
      "handheld-optimization-required-platform",
      localizedStrings.handheldOptimizationPlatformRequired,
      function (platformValues) {
        return hasHandheldOptimizationRequiredPlatform(
          this.parent?.handheldOptimization,
          platformValues
        );
      }
    );

/** *********************************** Common Introduction Validation Schemas ************************************************/
export const commonIntroSchemas = (localizedStrings: { [key: string]: string }) => {
  const maxLengthMessageByField = (maxLengthLimit: number) =>
    format(localizedStrings?.maxLengthRequiredErrorMessage ?? "", `${maxLengthLimit}`);

  return {
    contact: {
      firstName: Yup.string()
        .required(localizedStrings.required)
        .max(
          contactInputsMaxLength.firstName,
          maxLengthMessageByField(contactInputsMaxLength.firstName)
        )
        .test(
          "no-blocked-chars",
          localizedStrings.blockedCharactersError ?? "Cannot contain: < > % $ [ ] { } ?",
          value => !value || !blockedCharsRegex.test(value)
        ),
      lastName: Yup.string()
        .required(localizedStrings.required)
        .max(
          contactInputsMaxLength.lastName,
          maxLengthMessageByField(contactInputsMaxLength.lastName)
        )
        .test(
          "no-blocked-chars",
          localizedStrings.blockedCharactersError ?? "Cannot contain: < > % $ [ ] { } ?",
          value => !value || !blockedCharsRegex.test(value)
        ),
      businessTitle: Yup.string().required(localizedStrings.required),
      email: Yup.string()
        .required(localizedStrings.required)
        .max(contactInputsMaxLength.email, maxLengthMessageByField(contactInputsMaxLength.email))
        .matches(validEmailRegex, localizedStrings.invalidEmailFormat),
      // Address
      addressLine1: Yup.string()
        .required(localizedStrings.required)
        .max(
          contactInputsMaxLength.addressLine1,
          maxLengthMessageByField(contactInputsMaxLength.addressLine1)
        )
        .test(
          "no-blocked-chars",
          localizedStrings.blockedCharactersError ?? "Cannot contain: < > % $ [ ] { } ?",
          value => !value || !blockedCharsRegex.test(value)
        ),
      addressLine2: Yup.string()
        .nullable()
        .max(
          contactInputsMaxLength.addressLine2,
          maxLengthMessageByField(contactInputsMaxLength.addressLine2)
        )
        .test(
          "no-blocked-chars",
          localizedStrings.blockedCharactersError ?? "Cannot contain: < > % $ [ ] { } ?",
          value => !value || !blockedCharsRegex.test(value)
        ),
      city: Yup.string()
        .required(localizedStrings.required)
        .max(contactInputsMaxLength.city, maxLengthMessageByField(contactInputsMaxLength.city))
        .test(
          "no-blocked-chars",
          localizedStrings.blockedCharactersError ?? "Cannot contain: < > % $ [ ] { } ?",
          value => !value || !blockedCharsRegex.test(value)
        ),
      stateRegion: Yup.string()
        .required(localizedStrings.required)
        .when("countryOrRegion", (countryCode: string, schema: Yup.StringSchema) => {
          const isoCode = countryCrmCodeToIso[countryCode];
          if (isoCode === "US" || isoCode === "CA") {
            return schema.matches(
              stateProvinceRegex,
              localizedStrings.invalidStateProvince ?? "Must be exactly 2 letters (e.g. WA, ON)"
            );
          }
          return schema;
        }),
      postalCode: Yup.string().required(localizedStrings.required),
      countryOrRegion: Yup.string().required(localizedStrings.required),
      phone: Yup.string()
        .required(localizedStrings.required)
        .max(contactInputsMaxLength.phone, maxLengthMessageByField(contactInputsMaxLength.phone))
        .test(
          "phone-min-digits",
          localizedStrings.phoneMinDigitsError ?? "Phone number must have at least 4 digits",
          value => {
            if (!value) {
              return true;
            }
            // Strip country code prefix (e.g., "+506 " or "+1 ") to count only user-input digits
            // Require at least one space after +{code} to identify a prefix
            const withoutPrefix = value.replace(/^\+\d+\s+/, "");
            const digits = withoutPrefix.replace(/\D/g, "");
            return digits.length >= 4;
          }
        )
        .when("countryOrRegion", (countryCode: string, schema: Yup.StringSchema) => {
          const isoCode = countryCrmCodeToIso[countryCode];
          if (!isoCode) {
            return schema;
          }
          // Use US validation for Puerto Rico (same country code +1)
          const validationIso = isoCode === "PR" ? "US" : isoCode;
          const countryData = countryValidations[
            validationIso as keyof typeof countryValidations
          ] as { PhoneNumberRegex?: string; CountryCallingCodes?: string } | undefined;
          if (countryData?.PhoneNumberRegex) {
            const regex = new RegExp(countryData.PhoneNumberRegex);
            const callingCode =
              (
                countryValidations[isoCode as keyof typeof countryValidations] as
                  | { CountryCallingCodes?: string }
                  | undefined
              )?.CountryCallingCodes ?? countryData.CountryCallingCodes;

            const countryErrorMessages: Record<string, string> = {
              US:
                localizedStrings.invalidUSPhoneNumber ??
                "Invalid United States number, 10 digits are required",
              CA:
                localizedStrings.invalidCAPhoneNumber ??
                "Invalid Canada number, 10 digits are required",
              PR:
                localizedStrings.invalidPRPhoneNumber ??
                "Invalid Puerto Rico number, 10 digits are required",
            };
            const errorMessage =
              countryErrorMessages[isoCode] ??
              localizedStrings.invalidPhoneNumberFormat ??
              "Invalid phone number format";

            return schema.test("phone-country-format", errorMessage, value => {
              if (!value) {
                return true;
              }

              // Require "+{callingCode}" prefix so we don't submit local numbers without a calling code.
              const escapedCallingCode = String(callingCode ?? "").replace(
                /[.*+?^${}()|[\]\\]/g,
                "\\$&"
              );
              const prefixRegex = new RegExp(`^\\+${escapedCallingCode}\\s*`);
              if (!prefixRegex.test(value)) {
                return false;
              }

              const phoneDigits = value.replace(prefixRegex, "");
              return regex.test(phoneDigits);
            });
          }
          return schema;
        }),
      useDifferentShippingAddress: Yup.boolean(),
      shippingAddressLine1: Yup.string()
        .nullable()
        .when("useDifferentShippingAddress", {
          is: true,
          then: Yup.string().required(localizedStrings.required),
        }),
      shippingCity: Yup.string()
        .nullable()
        .when("useDifferentShippingAddress", {
          is: true,
          then: Yup.string().required(localizedStrings.required),
        }),
      shippingStateRegion: Yup.string()
        .nullable()
        .when("useDifferentShippingAddress", {
          is: true,
          then: Yup.string().required(localizedStrings.required),
        }),
      shippingPostalCode: Yup.string()
        .nullable()
        .when("useDifferentShippingAddress", {
          is: true,
          then: Yup.string().required(localizedStrings.required),
        }),
      shippingCountryOrRegion: Yup.string()
        .nullable()
        .when("useDifferentShippingAddress", {
          is: true,
          then: Yup.string().required(localizedStrings.required),
        }),
      shippingPhone: Yup.string()
        .nullable()
        .when("useDifferentShippingAddress", {
          is: true,
          then: Yup.string().required(localizedStrings.required),
        })
        .matches(validPhoneNumberRegex, localizedStrings.invalidPhoneNumberFormat),
    },
    studio: {
      studioName: Yup.string().required(localizedStrings.required),
      partnerCenterVerifiedStudioName: Yup.string().required(),
      businessName: Yup.string().required(localizedStrings.required),
      studioWebsite: Yup.string()
        .nullable()
        .test(
          "is-url",
          localizedStrings.pleaseAddValidUrlValidation,
          value => !value || value.trim() === "" || validUrlRegex.test(value)
        ),
    },
  };
};

/** *********************************** ID@Xbox Introduction Validation Schemas ************************************************/
export const idXboxIntroValidationSchemas = (localizedStrings: { [key: string]: string }) => {
  const introSchemas = commonIntroSchemas(localizedStrings);
  return {
    contact: introSchemas.contact,
    studio: {
      ...introSchemas.studio,
      thisCompanyActsAs: Yup.array()
        .min(1, localizedStrings.required)
        .required(localizedStrings.required),
      publicTrailersLinks: Yup.array().of(
        Yup.string()
          .nullable()
          .test(
            "is-url",
            localizedStrings.pleaseAddValidUrlValidation,
            value => !value || value.trim() === "" || validUrlRegex.test(value)
          )
      ),
      middlewareEnginesUsed: Yup.string().required(localizedStrings.required),
    },
  };
};

/** *********************************** ID@Xbox Game Information Validation Schemas ************************************************/

export const idXboxGameInformationValidationSchemas = (localizedStrings: {
  [key: string]: string;
}) => {
  const maxLengthMessageByField = (maxLengthLimit: number) =>
    format(localizedStrings?.maxLengthRequiredErrorMessage ?? "", `${maxLengthLimit}`);
  const minValueMessage = (min: number) =>
    format(localizedStrings?.minValueErrorMessage ?? "", `${min}`);
  const maxValueMessage = (max: number) =>
    format(localizedStrings?.maxValueErrorMessage ?? "", `${max}`);

  return {
    details: {
      gameTitle: Yup.string()
        .required(localizedStrings.required)
        .test("is-specific-game-title", localizedStrings.invalidGameTitle, isSpecificGameTitle)
        .max(
          gameConceptInputsMaxLength.gameTitle,
          maxLengthMessageByField(gameConceptInputsMaxLength.gameTitle)
        ),
      genre: Yup.number()
        .nullable(true)
        .transform((_, val) => (val === val ? val : null)),
      anticipatedRatings: Yup.number()
        .nullable(true)
        .transform((_, val) => (val === val ? val : null)),
      gameDevelopers: Yup.array().of(
        Yup.string().max(
          gameConceptInputsMaxLength.gameDevelopers,
          maxLengthMessageByField(gameConceptInputsMaxLength.gameDevelopers)
        )
      ),
      hook: Yup.string()
        .required(localizedStrings.required)
        .max(
          gameConceptInputsMaxLength.hook,
          maxLengthMessageByField(gameConceptInputsMaxLength.hook)
        ),
      gameplayVideosLinks: Yup.array()
        .of(
          Yup.string()
            .required(localizedStrings.pleaseAddValidUrlValidation)
            .matches(validUrlRegex, localizedStrings.pleaseAddValidUrlValidation)
            .max(
              gameConceptInputsMaxLength.gameplayVideosLinks,
              maxLengthMessageByField(gameConceptInputsMaxLength.gameplayVideosLinks)
            )
        )
        .min(1, localizedStrings.pleaseAddOneLinkValidation)
        .required(),
      gameplayDetails: Yup.string()
        .required(localizedStrings.required)
        .max(
          gameConceptInputsMaxLength.gameplayDetails,
          maxLengthMessageByField(gameConceptInputsMaxLength.gameplayDetails)
        ),
      gameBuildLinks: Yup.array()
        .of(
          Yup.string()
            .required(localizedStrings.required)
            .matches(validUrlRegex, localizedStrings.pleaseAddValidUrlValidation)
            .max(
              gameConceptInputsMaxLength.gameBuildLinks,
              maxLengthMessageByField(gameConceptInputsMaxLength.gameBuildLinks)
            )
        )
        .min(1, localizedStrings.required)
        .required(),
      gameplayDuration: Yup.string()
        .required(localizedStrings.required)
        .max(
          gameConceptInputsMaxLength.gameplayDuration,
          maxLengthMessageByField(gameConceptInputsMaxLength.gameplayDuration)
        ),
      levelsDetails: Yup.string()
        .required(localizedStrings.required)
        .max(
          gameConceptInputsMaxLength.levelsDetails,
          maxLengthMessageByField(gameConceptInputsMaxLength.levelsDetails)
        ),
      levelsNumber: Yup.number()
        .nullable()
        .min(1, minValueMessage(1))
        .max(1000, maxValueMessage(1000)),
      steamLink: Yup.string()
        .nullable()
        .test(
          "is-url",
          localizedStrings.pleaseAddValidUrlValidation,
          value => !value || value.trim() === "" || validUrlRegex.test(value)
        ),
      itchIoLink: Yup.string()
        .nullable()
        .test(
          "is-url",
          localizedStrings.pleaseAddValidUrlValidation,
          value => !value || value.trim() === "" || validUrlRegex.test(value)
        ),
    },
    gameExpectations: {
      xboxPlayAnywhere: Yup.string().required(localizedStrings.required),
      handheldOptimization: Yup.string().required(localizedStrings.required),
      targetPlatforms: targetPlatformsValidationSchema(localizedStrings),
      middleware: Yup.number()
        .min(1, localizedStrings.required)
        .required(localizedStrings.required),
      releaseNotes: Yup.string().max(
        gameConceptInputsMaxLength.releaseNotes,
        maxLengthMessageByField(gameConceptInputsMaxLength.releaseNotes)
      ),
    },
    pricingAndAvailability: {
      monetizationStrategy: Yup.string().required(localizedStrings.required),
      suggestedRetailPrice: Yup.string()
        .nullable(true)
        .when("monetizationStrategy", {
          is: PricingOptions.Premium,
          then: Yup.string()
            .required(localizedStrings.required)
            .test(
              "suggested-price-validation",
              localizedStrings.suggestedRetailPriceErrorMessage,
              value => (value ? parseFloat(value?.replace(/[$,]/g, "")) >= 4.99 : !!value)
            ),
        }),
      howItWillGenerateMoney: Yup.string().when("monetizationStrategy", {
        is: PricingOptions.FreeToPlay,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.howItWillGenerateMoney,
            maxLengthMessageByField(gameConceptInputsMaxLength.howItWillGenerateMoney)
          )
          .required(localizedStrings.required),
      }),
      retentionMechanics: Yup.string().when("monetizationStrategy", {
        is: PricingOptions.FreeToPlay,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.retentionMechanics,
            maxLengthMessageByField(gameConceptInputsMaxLength.retentionMechanics)
          )
          .required(localizedStrings.required),
      }),
      hasInGameStore: Yup.boolean(),
      inGamePricePoints: Yup.string().when("hasInGameStore", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.inGamePricePoints,
            maxLengthMessageByField(gameConceptInputsMaxLength.inGamePricePoints)
          )
          .required(localizedStrings.required),
      }),
      whatPlayersCanSpendOn: Yup.string().when("hasInGameStore", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.whatPlayersCanSpendOn,
            maxLengthMessageByField(gameConceptInputsMaxLength.whatPlayersCanSpendOn)
          )
          .required(localizedStrings.required),
      }),
      timeUntilAPlayerIsPromptedToPay: Yup.string().when("hasInGameStore", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.timeUntilAPlayerIsPromptedToPay,
            maxLengthMessageByField(gameConceptInputsMaxLength.timeUntilAPlayerIsPromptedToPay)
          )
          .required(localizedStrings.required),
      }),
      hasSubscriptionFee: Yup.boolean(),
      subscriptionModel: Yup.string().when("hasSubscriptionFee", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.subscriptionModel,
            maxLengthMessageByField(gameConceptInputsMaxLength.subscriptionModel)
          )
          .required(localizedStrings.required),
      }),
      canPlayersTradeOrGiftItems: Yup.boolean(),
      howItemsCanBeGifted: Yup.string().when("canPlayersTradeOrGiftItems", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.howItemsCanBeGifted,
            maxLengthMessageByField(gameConceptInputsMaxLength.howItemsCanBeGifted)
          )
          .required(localizedStrings.required),
      }),
      supportedLanguages: Yup.array().of(Yup.string()),
      hasOtherLanguages: Yup.boolean(),
      otherLanguages: Yup.string().when("hasOtherLanguages", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.otherLanguages,
            maxLengthMessageByField(gameConceptInputsMaxLength.otherLanguages)
          )
          .required(localizedStrings.required),
      }),
      sensitiveContent: Yup.string()
        .nullable(true)
        .transform((_, val) => (val === val ? val : null)),
      sensitiveContentDetails: Yup.string().when("sensitiveContent", {
        is: (value: string) =>
          [
            SensitiveContentStatic.Yes.toString(),
            SensitiveContentStatic.Maybe.toString(),
            SensitiveContentStatic.Other.toString(),
          ].includes(value),
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.sensitiveContentDetails,
            maxLengthMessageByField(gameConceptInputsMaxLength.sensitiveContentDetails)
          )
          .required(localizedStrings.required),
      }),
    },
    technicalCapabilities: {
      anticipatedCapabilities: Yup.array().of(
        Yup.object().shape({
          capability: Yup.string().required(),
          selected: Yup.boolean().required(),
          details: Yup.string().when(["selected", "capability"], {
            is: (selected: boolean, capability: string) => selected && capability !== "hdr",
            then: Yup.string().required(localizedStrings.required),
          }),
        })
      ),
      xboxSeriesXSVersionCapabilities: Yup.array().of(
        Yup.object().shape({
          capability: Yup.string().required(),
          selected: Yup.boolean().required(),
          details: Yup.string().when("selected", {
            is: true,
            then: Yup.string().required(localizedStrings.required),
          }),
        })
      ),
    },
    configurations: {
      localPlayerModes: Yup.array().of(
        Yup.object().shape({
          playerMode: Yup.string().required(),
          selected: Yup.boolean().required(),
          playersPerConsole: Yup.number()
            .transform((newValue, originalValue) => (!originalValue ? undefined : newValue))
            .when(["playerMode", "selected"], {
              is: (playerMode: string, selected: boolean) =>
                selected === true && playerMode !== LocalPlayerModes.singleMultiplayer,
              then: Yup.number().required(localizedStrings.required),
            }),
        })
      ),
      onlinePlayerModes: Yup.array().of(
        Yup.object().shape({
          playerMode: Yup.string().required(),
          selected: Yup.boolean().required(),
          playersPerConsole: Yup.number()
            .transform((newValue, originalValue) => (!originalValue ? undefined : newValue))
            .when(["playerMode", "selected"], {
              is: (playerMode: string, selected: boolean) =>
                selected === true && playerMode !== OnlinePlayerModes.onlineSinglePlayer,
              then: Yup.number().required(localizedStrings.required),
            }),
        })
      ),
    },
    xboxNetwork: {},
    ugc: {
      hasUserGeneratedShareableContent: Yup.boolean(),
      ugcDetails: Yup.string().when("hasUserGeneratedShareableContent", {
        is: true,
        then: Yup.string()
          .nullable()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.ugcDetails,
            maxLengthMessageByField(gameConceptInputsMaxLength.ugcDetails)
          )
          .required(localizedStrings.required),
      }),
    },
    supplementaryData: {},
  };
};

/** *********************************** AAA Game Information Validation Schemas ************************************************/

export const aaaGameInformationValidationSchemas = (localizedStrings: {
  [key: string]: string;
}) => {
  const maxLengthMessageByField = (maxLengthLimit: number) =>
    format(localizedStrings?.maxLengthRequiredErrorMessage ?? "", `${maxLengthLimit}`);

  return {
    details: {
      gameTitle: Yup.string()
        .required(localizedStrings.required)
        .test("is-specific-game-title", localizedStrings.invalidGameTitle, isSpecificGameTitle)
        .max(
          gameConceptInputsMaxLength.gameTitle,
          maxLengthMessageByField(gameConceptInputsMaxLength.gameTitle)
        ),
      genre: Yup.number()
        .nullable(true)
        .transform((_, val) => (val === val ? val : null)),
      anticipatedRatings: Yup.number()
        .nullable(true)
        .transform((_, val) => (val === val ? val : null)),
      gameDevelopers: Yup.array().of(
        Yup.string().max(
          gameConceptInputsMaxLength.gameDevelopers,
          maxLengthMessageByField(gameConceptInputsMaxLength.gameDevelopers)
        )
      ),
      hook: Yup.string()
        .required(localizedStrings.required)
        .max(
          gameConceptInputsMaxLength.hook,
          maxLengthMessageByField(gameConceptInputsMaxLength.hook)
        ),
      gameplayVideosLinks: Yup.array().of(
        Yup.string()
          .matches(validUrlRegex, {
            message: localizedStrings.pleaseAddValidUrlValidation,
            excludeEmptyString: true,
          })
          .max(
            gameConceptInputsMaxLength.gameplayVideosLinks,
            maxLengthMessageByField(gameConceptInputsMaxLength.gameplayVideosLinks)
          )
      ),

      gameplayDetails: Yup.string()
        .required(localizedStrings.required)
        .max(
          gameConceptInputsMaxLength.gameplayDetails,
          maxLengthMessageByField(gameConceptInputsMaxLength.gameplayDetails)
        ),
      gameBuildLinks: Yup.array()
        .of(
          Yup.string()
            .required(localizedStrings.required)
            .matches(validUrlRegex, localizedStrings.pleaseAddValidUrlValidation)
            .max(
              gameConceptInputsMaxLength.gameBuildLinks,
              maxLengthMessageByField(gameConceptInputsMaxLength.gameBuildLinks)
            )
        )
        .min(1, localizedStrings.required)
        .required(),
      gameplayDuration: Yup.string()
        .required(localizedStrings.required)
        .max(
          gameConceptInputsMaxLength.gameplayDuration,
          maxLengthMessageByField(gameConceptInputsMaxLength.gameplayDuration)
        ),
    },
    distributionInformation: {
      shippingStrategy: Yup.string().required(localizedStrings.required),
      howItWillGenerateMoney: Yup.string().when("shippingStrategy", {
        is: AaaShippingType.digitalOnlyF2P,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.howItWillGenerateMoney,
            maxLengthMessageByField(gameConceptInputsMaxLength.howItWillGenerateMoney)
          )
          .required(localizedStrings.required),
      }),
      retentionMechanics: Yup.string().when("shippingStrategy", {
        is: AaaShippingType.digitalOnlyF2P,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.retentionMechanics,
            maxLengthMessageByField(gameConceptInputsMaxLength.retentionMechanics)
          )
          .required(localizedStrings.required),
      }),
      hasInGameStore: Yup.boolean(),
      inGamePricePoints: Yup.string().when("hasInGameStore", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.inGamePricePoints,
            maxLengthMessageByField(gameConceptInputsMaxLength.inGamePricePoints)
          )
          .required(localizedStrings.required),
      }),
      whatPlayersCanSpendOn: Yup.string().when("hasInGameStore", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.whatPlayersCanSpendOn,
            maxLengthMessageByField(gameConceptInputsMaxLength.whatPlayersCanSpendOn)
          )
          .required(localizedStrings.required),
      }),
      timeUntilAPlayerIsPromptedToPay: Yup.string().when("hasInGameStore", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.timeUntilAPlayerIsPromptedToPay,
            maxLengthMessageByField(gameConceptInputsMaxLength.timeUntilAPlayerIsPromptedToPay)
          )
          .required(localizedStrings.required),
      }),
      hasSubscriptionFee: Yup.boolean(),
      subscriptionModel: Yup.string().when("hasSubscriptionFee", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.subscriptionModel,
            maxLengthMessageByField(gameConceptInputsMaxLength.subscriptionModel)
          )
          .required(localizedStrings.required),
      }),
      canPlayersTradeOrGiftItems: Yup.boolean(),
      howItemsCanBeGifted: Yup.string().when("canPlayersTradeOrGiftItems", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.howItemsCanBeGifted,
            maxLengthMessageByField(gameConceptInputsMaxLength.howItemsCanBeGifted)
          )
          .required(localizedStrings.required),
      }),
      prevReleases: Yup.string().when("isPrevIndependentlyPublished", {
        is: YesNoChoice.yes.toString(),
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.prevReleases,
            maxLengthMessageByField(gameConceptInputsMaxLength.prevReleases)
          )
          .required(localizedStrings.required),
      }),
      primaryPublisherName: Yup.string().when("isAssumingResponsibilitesForPrevPublishedGame", {
        is: YesNoChoice.no.toString(),
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.primaryPublisherName,
            maxLengthMessageByField(gameConceptInputsMaxLength.primaryPublisherName)
          )
          .required(localizedStrings.required),
      }),
      certResponsiblePartner: Yup.string().when("isResponsibleForCertification", {
        is: YesNoChoice.no.toString(),
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.certResponsiblePartner,
            maxLengthMessageByField(gameConceptInputsMaxLength.certResponsiblePartner)
          )
          .required(localizedStrings.required),
      }),
    },
    gameExpectations: {
      xboxPlayAnywhere: Yup.string().required(localizedStrings.required),
      handheldOptimization: Yup.string().required(localizedStrings.required),
      targetPlatforms: targetPlatformsValidationSchema(localizedStrings),
      middleware: Yup.number()
        .min(1, localizedStrings.required)
        .required(localizedStrings.required),
    },
    availability: {
      supportedLanguages: Yup.array().of(Yup.string()),
      hasOtherLanguages: Yup.boolean(),
      otherLanguages: Yup.string().when("hasOtherLanguages", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.otherLanguages,
            maxLengthMessageByField(gameConceptInputsMaxLength.otherLanguages)
          )
          .required(localizedStrings.required),
      }),
      sensitiveContent: Yup.string()
        .nullable(true)
        .transform((_, val) => (val === val ? val : null)),
      sensitiveContentDetails: Yup.string().when("sensitiveContent", {
        is: (value: string) =>
          [
            SensitiveContentStatic.Yes.toString(),
            SensitiveContentStatic.Maybe.toString(),
            SensitiveContentStatic.Other.toString(),
          ].includes(value),
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.sensitiveContentDetails,
            maxLengthMessageByField(gameConceptInputsMaxLength.sensitiveContentDetails)
          )
          .required(localizedStrings.required),
      }),
    },
    technicalCapabilities: {
      anticipatedCapabilities: Yup.array().of(
        Yup.object().shape({
          capability: Yup.string().required(),
          selected: Yup.boolean().required(),
          details: Yup.string().when(["selected", "capability"], {
            is: (selected: boolean, capability: string) => selected && capability !== "hdr",
            then: Yup.string()
              .min(1, localizedStrings.required)
              .required(localizedStrings.required),
          }),
        })
      ),
      xboxSeriesXSVersionCapabilities: Yup.array().of(
        Yup.object().shape({
          capability: Yup.string().required(),
          selected: Yup.boolean().required(),
          details: Yup.string().when("selected", {
            is: true,
            then: Yup.string()
              .min(1, localizedStrings.required)
              .required(localizedStrings.required),
          }),
        })
      ),
      xboxSeriesXSAdvantage: Yup.string().max(
        gameConceptInputsMaxLength.xboxSeriesXSAdvantage,
        maxLengthMessageByField(gameConceptInputsMaxLength.xboxSeriesXSAdvantage)
      ),
      xboxSeriesXPerfTargets: Yup.string().max(
        gameConceptInputsMaxLength.xboxSeriesXPerfTargets,
        maxLengthMessageByField(gameConceptInputsMaxLength.xboxSeriesXPerfTargets)
      ),
      xboxSeriesSPerfTargets: Yup.string().max(
        gameConceptInputsMaxLength.xboxSeriesSPerfTargets,
        maxLengthMessageByField(gameConceptInputsMaxLength.xboxSeriesSPerfTargets)
      ),
      internetConnectionDetails: Yup.string().when("isInternetConnectionRequired", {
        is: true,
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.internetConnectionDetails,
            maxLengthMessageByField(gameConceptInputsMaxLength.internetConnectionDetails)
          )
          .required(localizedStrings.required),
      }),
    },
    configurations: {
      localPlayerModes: Yup.array().of(
        Yup.object().shape({
          playerMode: Yup.string().min(1, localizedStrings.required).required(),
          selected: Yup.boolean().required(),
          playersPerConsole: Yup.number()
            .transform((newValue, originalValue) => (!originalValue ? undefined : newValue))
            .when(["playerMode", "selected"], {
              is: (playerMode: string, selected: boolean) =>
                selected === true && playerMode !== LocalPlayerModes.singleMultiplayer,
              then: Yup.number().required(localizedStrings.required),
            }),
        })
      ),
      onlinePlayerModes: Yup.array().of(
        Yup.object().shape({
          playerMode: Yup.string().required(),
          selected: Yup.boolean().required(),
          playersPerConsole: Yup.number()
            .transform((newValue, originalValue) => (!originalValue ? undefined : newValue))
            .when(["playerMode", "selected"], {
              is: (playerMode: string, selected: boolean) =>
                selected === true && playerMode !== OnlinePlayerModes.onlineSinglePlayer,
              then: Yup.number().required(localizedStrings.required),
            }),
        })
      ),
    },
    xboxNetwork: {},
    ugc: {
      hasUserGeneratedShareableContent: Yup.boolean(),
      ugcDetails: Yup.string().when("hasUserGeneratedShareableContent", {
        is: true,
        then: Yup.string()
          .nullable()
          .min(1, localizedStrings.required)
          .max(
            gameConceptInputsMaxLength.ugcDetails,
            maxLengthMessageByField(gameConceptInputsMaxLength.ugcDetails)
          )
          .required(localizedStrings.required),
      }),
    },
    supplementaryData: {},
  };
};

/** *********************************** Pitch Validation Schemas ************************************************/

export const pitchInformationValidationSchemas = (localizedStrings: { [key: string]: string }) => {
  const maxLengthMessageByField = (maxLengthLimit: number) =>
    format(localizedStrings?.maxLengthRequiredErrorMessage ?? "", `${maxLengthLimit}`);

  return {
    publisherDeveloper: {
      nameCompanyOrganization: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.nameCompanyOrganization,
          maxLengthMessageByField(pitchInputsMaxLength.nameCompanyOrganization)
        ),
      emailAddress: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.emailAddress,
          maxLengthMessageByField(pitchInputsMaxLength.emailAddress)
        ),
      xboxContact: Yup.string(),
      programConsiderationType: Yup.array().of(Yup.string()),
      programConsiderationTypeOther: Yup.string().when("programConsiderationType", {
        is: (attribute1: string[]) => (attribute1 ? attribute1.includes("Other") : false),
        then: Yup.string()
          .min(1, localizedStrings.required)
          .required(localizedStrings.required)
          .max(
            pitchInputsMaxLength.programConsiderationTypeOther,
            maxLengthMessageByField(pitchInputsMaxLength.programConsiderationTypeOther)
          ),
        otherwise: Yup.string().nullable(true),
      }),
      reasonOfGoodFit: Yup.string().max(
        pitchInputsMaxLength.reasonOfGoodFit,
        maxLengthMessageByField(pitchInputsMaxLength.reasonOfGoodFit)
      ),
      specificAspectToPrototype: Yup.string().max(
        pitchInputsMaxLength.specificAspectToPrototype,
        maxLengthMessageByField(pitchInputsMaxLength.specificAspectToPrototype)
      ),
      studioPointOfContact: Yup.string().max(
        pitchInputsMaxLength.studioPointOfContact,
        maxLengthMessageByField(pitchInputsMaxLength.studioPointOfContact)
      ),
      studioSize: Yup.string().required(localizedStrings.required),
      countryLocated: Yup.string().max(
        pitchInputsMaxLength.countryLocated,
        maxLengthMessageByField(pitchInputsMaxLength.countryLocated)
      ),
      youHavePublisher: Yup.string().required(localizedStrings.required),
      publisherName: Yup.string().when("youHavePublisher", {
        is: (value: string) => value === "Yes",
        then: Yup.string()
          .min(1, localizedStrings.required)
          .required(localizedStrings.required)
          .max(
            pitchInputsMaxLength.publisherName,
            maxLengthMessageByField(pitchInputsMaxLength.publisherName)
          ),
        otherwise: Yup.string().nullable(true),
      }),
      studioEstablished: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.studioEstablished,
          maxLengthMessageByField(pitchInputsMaxLength.studioEstablished)
        ),
      introductionDeckStudio: Yup.string().max(
        pitchInputsMaxLength.introductionDeckStudio,
        maxLengthMessageByField(pitchInputsMaxLength.introductionDeckStudio)
      ),
      companyWebsite: Yup.string().max(
        pitchInputsMaxLength.companyWebsite,
        maxLengthMessageByField(pitchInputsMaxLength.companyWebsite)
      ),
      signedUpIdXbox: Yup.string().required(localizedStrings.required),
      kitAlready: Yup.string().required(localizedStrings.required),
      launchedForConsole: Yup.string().required(localizedStrings.required),
      listGameLaunchedOnConsole: Yup.string().when("launchedForConsole", {
        is: (value: string) => value === "Yes",
        then: Yup.string()
          .min(1, localizedStrings.required)
          .required(localizedStrings.required)
          .max(
            pitchInputsMaxLength.listGameLaunchedOnConsole,
            maxLengthMessageByField(pitchInputsMaxLength.listGameLaunchedOnConsole)
          ),
        otherwise: Yup.string().nullable(true),
      }),
    },
    aboutYourGame: {
      gameTitle: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.gameTitle,
          maxLengthMessageByField(pitchInputsMaxLength.gameTitle)
        ),
      containsNftOrBlockchain: Yup.string().required(localizedStrings.required),
      stageOfDevelopment: Yup.string().required(localizedStrings.required),
      releasedForOtherPlatforms: Yup.string().required(localizedStrings.required),
      releasedPlatforms: Yup.array().when("releasedForOtherPlatforms", {
        is: (attribute1: string) => attribute1 === "Yes",
        then: Yup.array().min(1, localizedStrings.required).required(localizedStrings.required),
        otherwise: Yup.array().nullable(true),
      }),
      releasedPlatformsOther: Yup.string().when("releasedPlatforms", {
        is: (attribute1: string[]) => (attribute1 ? attribute1.includes("Other") : false),
        then: Yup.string()
          .min(1, localizedStrings.required)
          .required(localizedStrings.required)
          .max(
            pitchInputsMaxLength.releasedPlatformsOther,
            maxLengthMessageByField(pitchInputsMaxLength.releasedPlatformsOther)
          ),
        otherwise: Yup.string().nullable(true),
      }),
      releasedPlatformsDate: Yup.string().nullable(true),
      announced: Yup.string().required(localizedStrings.required),
      targetedPlatforms: Yup.array().of(Yup.string()),
      steamReleaseWindow: Yup.string()
        .nullable(true)
        .max(
          pitchInputsMaxLength.steamReleaseWindow,
          maxLengthMessageByField(pitchInputsMaxLength.steamReleaseWindow)
        ),
      xboxReleaseWindow: Yup.string()
        .nullable(true)
        .max(
          pitchInputsMaxLength.xboxReleaseWindow,
          maxLengthMessageByField(pitchInputsMaxLength.xboxReleaseWindow)
        ),
      genre: Yup.array()
        .of(Yup.string().required())
        .min(1, localizedStrings.required)
        .required(localizedStrings.required),
      genreOther: Yup.string().when("genre", {
        is: (attribute1: string[]) => (attribute1 ? attribute1.includes("Other") : false),
        then: Yup.string()
          .min(1, localizedStrings.required)
          .required(localizedStrings.required)
          .max(
            pitchInputsMaxLength.genreOther,
            maxLengthMessageByField(pitchInputsMaxLength.genreOther)
          ),
        otherwise: Yup.string().nullable(true),
      }),
      gameEngine: Yup.string().required(localizedStrings.required),
      engineOther: Yup.string().when("gameEngine", {
        is: "Other",
        then: Yup.string()
          .min(1, localizedStrings.required)
          .required(localizedStrings.required)
          .max(
            pitchInputsMaxLength.engineOther,
            maxLengthMessageByField(pitchInputsMaxLength.engineOther)
          ),
      }),
      businessModel: Yup.array().of(Yup.string().required(localizedStrings.required)),
      gameplayDuration: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.gameplayDuration,
          maxLengthMessageByField(pitchInputsMaxLength.gameplayDuration)
        ),
      onlineMultiplayer: Yup.string().required(localizedStrings.required),
      gameIntroDeckLink: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.gameplayDuration,
          maxLengthMessageByField(pitchInputsMaxLength.gameIntroDeckLink)
        ),
      otherInfoLinks: Yup.array().of(
        Yup.string()
          .required(localizedStrings.required)
          .max(
            pitchInputsMaxLength.otherInfoLinks,
            maxLengthMessageByField(pitchInputsMaxLength.otherInfoLinks)
          )
      ),
      buildLinks: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.buildLinks,
          maxLengthMessageByField(pitchInputsMaxLength.buildLinks)
        ),
      videoLinks: Yup.string().max(
        pitchInputsMaxLength.videoLinks,
        maxLengthMessageByField(pitchInputsMaxLength.videoLinks)
      ),
      gameDescription: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.gameDescription,
          maxLengthMessageByField(pitchInputsMaxLength.gameDescription)
        ),
      targetAudience: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.targetAudience,
          maxLengthMessageByField(pitchInputsMaxLength.targetAudience)
        ),
      competitorTitles: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.competitorTitles,
          maxLengthMessageByField(pitchInputsMaxLength.competitorTitles)
        ),
      uniqueFactor: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.uniqueFactor,
          maxLengthMessageByField(pitchInputsMaxLength.uniqueFactor)
        ),
      feelingsEvoked: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.feelingsEvoked,
          maxLengthMessageByField(pitchInputsMaxLength.feelingsEvoked)
        ),
      pivotalMoment: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.pivotalMoment,
          maxLengthMessageByField(pitchInputsMaxLength.pivotalMoment)
        ),
      postLaunchSupportPlans: Yup.string()
        .required(localizedStrings.required)
        .max(
          pitchInputsMaxLength.postLaunchSupportPlans,
          maxLengthMessageByField(pitchInputsMaxLength.postLaunchSupportPlans)
        ),
      sensitiveContent: Yup.string().required(localizedStrings.required),
      sensitiveContentDetails: Yup.string().when("sensitiveContent", {
        is: (value: string) => value === "Yes" || value === "Maybe" || value === "Other",
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            pitchInputsMaxLength.sensitiveContentDetails,
            maxLengthMessageByField(pitchInputsMaxLength.sensitiveContentDetails)
          )
          .required(localizedStrings.required),
      }),
    },
    askForXbox: {
      fundingSought: Yup.string().required(localizedStrings.required),
      fundingSoughtDetails: Yup.string().when("fundingSought", {
        is: (value: string) => value === "Yes",
        then: Yup.string()
          .min(1, localizedStrings.required)
          .max(
            pitchInputsMaxLength.fundingSoughtDetails,
            maxLengthMessageByField(pitchInputsMaxLength.fundingSoughtDetails)
          )
          .required(localizedStrings.required),
      }),
      otherSupportNeeded: Yup.string().max(
        pitchInputsMaxLength.otherSupportNeeded,
        maxLengthMessageByField(pitchInputsMaxLength.otherSupportNeeded)
      ),
    },
    aboutYourself: {
      studioBackground: Yup.string().max(
        pitchInputsMaxLength.studioBackground,
        maxLengthMessageByField(pitchInputsMaxLength.studioBackground)
      ),
      activeInLocalGameDev: Yup.string().max(
        pitchInputsMaxLength.activeInLocalGameDev,
        maxLengthMessageByField(pitchInputsMaxLength.activeInLocalGameDev)
      ),
    },
  };
};

/** *********************************** Streaming EU Validation Schemas ************************************************/
export const streamingEuValidationSchemas = () => ({
  accessToEntitlementData: Yup.boolean().required(),
  companyName: Yup.string().when("accessToEntitlementData", {
    is: true,
    then: Yup.string().min(1).required(),
  }),
  contactPersonLastName: Yup.string().when("accessToEntitlementData", {
    is: true,
    then: Yup.string().min(1).required(),
  }),
  contactPersonFirstName: Yup.string().when("accessToEntitlementData", {
    is: true,
    then: Yup.string().min(1).required(),
  }),
  companyRole: Yup.string().when("accessToEntitlementData", {
    is: true,
    then: Yup.string().min(1).required(),
  }),
  contactEmail: Yup.string().when("accessToEntitlementData", {
    is: true,
    then: Yup.string().min(1).matches(validEmailRegex).required(),
  }),
  requestedEntitlementData: Yup.array().when("accessToEntitlementData", {
    is: true,
    then: Yup.array().of(Yup.string().required()).min(1).required(),
  }),
  agreesToTermsAndConditions: Yup.boolean().when("accessToEntitlementData", {
    is: true,
    then: Yup.boolean().required(),
  }),
});

/** *********************************** GDK Middleware Request Validation Schemas ************************************************/
export const gdkMiddlewareRequestValidationSchemas = () => ({
  sellerId: Yup.string().required(),
  middlewareId: Yup.string().required(),
  email: Yup.string().min(1).matches(validEmailRegex).required(),
});
